import { useTranslation } from 'react-i18next'
import { Checkbox, Radio, RadioGroup } from '@structural-climbing/ui'
import { MemberAvatar } from '../members/MemberAvatar'

/**
 * A member the gym added at the desk who may be the person behind a pending
 * join request: found by the account's verified email, or picked by staff
 * from those added with no email. The Worker checks either; staff decide.
 */
export interface DeskMatch {
  user_id: string
  name: string
  phone: string | null
  date_of_birth: string | null
  added_at: string
  active_plan: { name: string; entries_remaining: number | null; expires_at: string | null } | null
  check_in_count: number
  order_count: number
  /** The request gave the same phone number. */
  same_phone: boolean
  /** What the desk entry and the request disagree on. */
  differences: ('name' | 'date_of_birth')[]
}

/** A desk member staff may pick, before what would move is loaded. */
export type DeskCandidate = Omit<DeskMatch, 'active_plan' | 'check_in_count' | 'order_count'>

export type DeskMatchChoice = 'merge' | 'separate'

interface Props {
  match: DeskMatch
  /** Picked by staff rather than found by email. */
  picked?: boolean
  /** The request's own details, to show beside anything that differs. */
  requestName: string
  requestDateOfBirth: string | null
  choice: DeskMatchChoice | null
  onChoice: (choice: DeskMatchChoice) => void
  /** Ticked when staff checked a difference in person. */
  differencesChecked: boolean
  onDifferencesChecked: (checked: boolean) => void
}

/** Whether a "yes" is complete: any difference has been checked by staff. */
export function mergeConfirmed(match: DeskMatch, differencesChecked: boolean): boolean {
  return (match.differences ?? []).length === 0 || differencesChecked
}

/**
 * Asks whether the person asking to join is the one already added at the desk,
 * and says exactly what a "yes" moves. Nothing is picked for staff: merging two
 * people is not undone, so it has to be a choice someone made. When the name or
 * date of birth differ, staff also tick that they checked.
 */
export function DeskMatchBlock({
  match,
  picked = false,
  requestName,
  requestDateOfBirth,
  choice,
  onChoice,
  differencesChecked,
  onDifferencesChecked,
}: Props) {
  const { t, i18n } = useTranslation()
  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  // A date of birth is a calendar day, not an instant: never shift it by zone.
  const day = (ymd: string) =>
    new Date(`${ymd}T12:00:00Z`).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  const plan = match.active_plan
  // Missing from a Worker older than this app: the two deploy apart.
  const differences = match.differences ?? []
  const differs = differences.length > 0

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3">
      <p className="text-sm font-semibold">{t('requests.deskMatchTitle')}</p>

      <div className="flex items-center gap-3">
        {/* A desk member never uploaded a photo: initials. */}
        <MemberAvatar
          userId={match.user_id}
          name={match.name}
          avatarExt={null}
          avatarUpdatedAt={null}
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{match.name}</p>
          <p className="text-xs text-foreground/60">
            {t('requests.deskMatchAdded', { date: date(match.added_at) })}
          </p>
          <p className="text-xs text-foreground/60">
            {[
              match.phone &&
                (match.same_phone
                  ? `${match.phone} (${t('requests.deskLinkSamePhone')})`
                  : match.phone),
              match.date_of_birth &&
                t('requests.deskMatchBorn', { date: day(match.date_of_birth) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </div>
      <p className="text-xs text-foreground/60">
        {t(picked ? 'requests.deskMatchLinked' : 'requests.deskMatchSameEmail')}
      </p>

      {differs && (
        <div
          role="alert"
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          <p className="font-semibold">{t('requests.deskMatchDiffers')}</p>
          {differences.includes('name') && (
            <p>{t('requests.deskMatchDiffName', { desk: match.name, request: requestName })}</p>
          )}
          {differences.includes('date_of_birth') && (
            <p>
              {t('requests.deskMatchDiffDob', {
                desk: match.date_of_birth ? day(match.date_of_birth) : '',
                request: requestDateOfBirth ? day(requestDateOfBirth) : '',
              })}
            </p>
          )}
        </div>
      )}

      <RadioGroup
        aria-label={t('requests.deskMatchTitle')}
        value={choice ?? ''}
        onValueChange={(v) => onChoice(v as DeskMatchChoice)}
      >
        <Radio value="merge" description={t('requests.deskMatchRemoved')}>
          {t('requests.deskMatchYes')}
        </Radio>
        {choice === 'merge' && (
          <div className="ml-7 flex flex-col gap-0.5 text-sm" data-testid="desk-match-moves">
            <p className="text-xs text-foreground/60">{t('requests.deskMatchMoves')}</p>
            <p>
              {plan ? (
                <>
                  {t('requests.deskMatchPlan', { name: plan.name })}
                  {plan.entries_remaining !== null &&
                    ` · ${t('requests.deskMatchEntries', { count: plan.entries_remaining })}`}
                  {plan.expires_at &&
                    ` · ${t('requests.deskMatchPlanUntil', { date: date(plan.expires_at) })}`}
                </>
              ) : (
                t('requests.deskMatchNoPlan')
              )}
            </p>
            <p>
              {t('requests.deskMatchCheckIns', { count: match.check_in_count })} ·{' '}
              {t('requests.deskMatchPayments', { count: match.order_count })}
            </p>
            {differs && (
              <Checkbox
                className="mt-1"
                color="danger"
                isSelected={differencesChecked}
                onValueChange={onDifferencesChecked}
              >
                <span className="text-sm">{t('requests.deskMatchConfirmDiff')}</span>
              </Checkbox>
            )}
          </div>
        )}
        <Radio value="separate" description={t('requests.deskMatchNoHint')}>
          {t('requests.deskMatchNo')}
        </Radio>
      </RadioGroup>
    </div>
  )
}
